import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import {AutomationNode, NodeConnection, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {useTranslation} from 'react-i18next';
import {continueIfError} from '../config/continueIf';
import {isLegacyAutomationNode} from '../config/legacyAutomation';
import {getNodeColor, getNodeDisplay, isTriggerNode} from '../config/nodeCategories';
import {hasReportSurvey} from '../config/surveyReport';
import {hasSurveyPick} from '../config/surveyTriggers';
import {teamEmailWithInvalidAttachments, teamEmailWithoutContent, teamEmailWithoutTeam} from '../config/teamEmail';
import {hasTeamMemberPicks} from '../config/teamMembers';
import {scheduleCanFire} from '../utils/nextScheduledRun';
import {nodeSummaryRows} from './NodeSummary';

interface AutomationActivateDialogProps {
    open: boolean;
    nodes: AutomationNode[];
    /** The saved flow's connections: Send team email's checks depend on what comes before it. */
    connections?: NodeConnection[];
    hasUnsavedChanges: boolean;
    isActivating: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

interface GoLiveProblem {
    names: string[];
    one: (name: string) => string;
    many: (names: string) => string;
}

const NO_CONNECTIONS: NodeConnection[] = [];

const AutomationActivateDialog: React.FC<AutomationActivateDialogProps> = ({
    open,
    nodes,
    connections = NO_CONNECTIONS,
    hasUnsavedChanges,
    isActivating,
    onClose,
    onConfirm
}) => {
    const {t, i18n} = useTranslation();

    const triggers = nodes.filter(node => isTriggerNode(node.type));
    const actions = nodes.filter(node => !isTriggerNode(node.type));

    const namesOf = (matches: (node: AutomationNode) => boolean) => nodes
        .filter(matches)
        .map(node => node.label || t(getNodeDisplay(node.type).label));

    // Mirrors the API's go-live refusals (`AutomationService.validateFlowForActivation`), said before the request fails.
    const problems: GoLiveProblem[] = [
        {
            names: namesOf(node => node.type === NodeType.AUDIENCE && !node.config?.audienceId),
            one: name => t('The Audience step ‘{{name}}’ has no audience selected. Pick one before going live.', {name}),
            many: names => t('These Audience steps have no audience selected: {{names}}. Pick one for each before going live.', {names})
        },
        {
            // Explicit opt-in: with nobody picked it has nobody to fan out over.
            names: namesOf(node => node.type === NodeType.TEAM_MEMBERS && !hasTeamMemberPicks(node.config)),
            one: name => t('The Team members step ‘{{name}}’ has nobody picked. Pick who goes through it before going live.', {name}),
            many: names => t('These Team members steps have nobody picked: {{names}}. Pick who goes through each before going live.', {names})
        },
        {
            // There is no "any survey": without one the trigger never fires.
            names: namesOf(node => (node.type === NodeType.SURVEY_SUBMITTED || node.type === NodeType.SURVEY_REMINDER_DUE)
                && !hasSurveyPick(node.config)),
            one: name => t('The survey trigger ‘{{name}}’ has no survey selected. Pick one before going live.', {name}),
            many: names => t('These survey triggers have no survey selected: {{names}}. Pick one for each before going live.', {names})
        },
        {
            // The card's "No schedule configured": without a start the API's sweep never fires it.
            names: namesOf(node => node.type === NodeType.SCHEDULED_TRIGGER && !scheduleCanFire(node.config)),
            one: name => t('The scheduled trigger ‘{{name}}’ has no schedule configured. Pick when it starts before going live.', {name}),
            many: names => t('These scheduled triggers have no schedule configured: {{names}}. Pick when each starts before going live.', {names})
        },
        {
            names: namesOf(node => node.type === NodeType.SURVEY_REPORT && !hasReportSurvey(node.config)),
            one: name => t('The Survey report step ‘{{name}}’ has no survey selected. Pick one before going live.', {name}),
            many: names => t('These Survey report steps have no survey selected: {{names}}. Pick one for each before going live.', {names})
        },
        {
            // The recipient is the run's team member, so there has to be one.
            names: namesOf(node => teamEmailWithoutTeam(node, nodes, connections)),
            one: name => t('The Send team email step ‘{{name}}’ is not below a Team members step, so it has nobody to write to. Move it below one before going live.', {name}),
            many: names => t('These Send team email steps are not below a Team members step, so they have nobody to write to: {{names}}. Move each below one before going live.', {names})
        },
        {
            names: namesOf(node => teamEmailWithInvalidAttachments(node, nodes, connections)),
            one: name => t('The Send team email step ‘{{name}}’ selects unavailable attachment steps. Choose file steps that run before it on every path.', {name}),
            many: names => t('These Send team email steps select unavailable attachment steps: {{names}}. Choose file steps that run before each email on every path.', {names})
        },
        {
            names: namesOf(teamEmailWithoutContent),
            one: name => t('The Send team email step ‘{{name}}’ needs a subject and builder content before going live.', {name}),
            many: names => t('These Send team email steps need a subject and builder content before going live: {{names}}.', {names})
        },
        {
            names: namesOf(node => node.type === NodeType.CONTINUE_IF && !!continueIfError(node.config ?? {})),
            one: name => t('The Continue if step ‘{{name}}’ has no complete condition. Finish it before going live.', {name}),
            many: names => t('These Continue if steps have no complete condition: {{names}}. Finish each before going live.', {names})
        },
        {
            // An Audience with an inline filter is legacy too, but the Audience problem already names it.
            names: namesOf(node => isLegacyAutomationNode(node) && node.type !== NodeType.AUDIENCE),
            one: name => t('The step ‘{{name}}’ uses a retired step type. Replace it with an Audience step before going live.', {name}),
            many: names => t('These steps use a retired step type: {{names}}. Replace them with an Audience step before going live.', {names})
        },
    ];

    const actionCounts = actions.reduce<{type: NodeType; count: number}[]>((counts, node) => {
        const existing = counts.find(entry => entry.type === node.type);
        if (existing) {
            existing.count += 1;
        } else {
            counts.push({type: node.type, count: 1});
        }
        return counts;
    }, []);

    const stepCountLabel = actions.length === 1
        ? t('1 step')
        : t('{{total}} steps', {total: actions.length});

    const actionSummary = actionCounts
        .map(entry => `${entry.count} × ${t(getNodeDisplay(entry.type).label)}`)
        .join(', ');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={'sm'}
            fullWidth
            PaperProps={{sx: {maxHeight: '90vh'}}}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Typography variant={'h6'} fontWeight={600} noWrap>
                        {t('Set this automation live')}
                    </Typography>

                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Stack gap={2} sx={{mt: 1}}>
                    {triggers.length ? (
                        <Box>
                            <Typography variant={'subtitle2'} sx={{mb: 1}}>
                                {t('When it runs')}
                            </Typography>

                            <Stack gap={1}>
                                {triggers.map(node => {
                                    const display = getNodeDisplay(node.type);
                                    const hint = nodeSummaryRows(node.type, node.config, t, i18n)
                                        .map(row => (row.label ? `${row.label}: ${row.text}` : row.text))
                                        .join(' · ');

                                    return (
                                        <Stack
                                            key={node.id}
                                            direction={'row'}
                                            alignItems={'center'}
                                            gap={1}
                                        >
                                            {React.cloneElement(display.icon, {
                                                sx: {fontSize: 18, color: getNodeColor(node.type), flexShrink: 0}
                                            })}

                                            <Typography variant={'body2'} noWrap>
                                                {node.label || t(display.label)}
                                            </Typography>

                                            {!!hint && (
                                                <Typography variant={'body2'} color={'text.secondary'} noWrap>
                                                    {hint}
                                                </Typography>
                                            )}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ) : (
                        <Alert severity={'error'}>
                            {t('This automation has no trigger, so it cannot go live. Add a trigger first.')}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant={'subtitle2'} sx={{mb: 0.5}}>
                            {t('What it runs')}
                        </Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {actions.length
                                ? `${stepCountLabel}: ${actionSummary}`
                                : t('No steps configured yet.')}
                        </Typography>
                    </Box>

                    {problems.filter(problem => problem.names.length).map((problem, index) => (
                        <Alert key={index} severity={'error'}>
                            {problem.names.length === 1
                                ? problem.one(problem.names[0])
                                : problem.many(problem.names.join(', '))}
                        </Alert>
                    ))}

                    {hasUnsavedChanges && (
                        <Alert severity={'warning'}>
                            {t('You have unsaved changes. The last saved version is the one that becomes active.')}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    variant={'contained'}
                    loading={isActivating}
                    disabled={!triggers.length || problems.some(problem => problem.names.length)}
                    onClick={onConfirm}
                >
                    {t('Go live')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default AutomationActivateDialog;
